// Configuración de estados de reserva: color, etiqueta corta y si el día del
// calendario se pinta relleno (fill) o sólo con borde (outline).
// Se usa tanto en el listado por obra como en el detalle con calendario.
// Los valores de `key` coinciden con Reservation.Status del backend.
//
// Los colores salen del mockup "Línea de tiempo de la obra" y se agrupan por
// tramo del negocio; NO cambiarlos sin revisar ese diseño:
//   blanco  #FFFFFF -> disponibilidad (aún no hay reserva)
//   verde   #40B04C -> solicitud, confirmación y firmas
//   azul    #0070B9 -> facturas y pagos entre la administración y el comprador
//   morado  #7661A7 -> estreno de la función
//   magenta #FF1AFF -> facturas y pagos entre la administración y la compañía
export const TIMELINE_COLORS = {
  available: "#FFFFFF",
  agreement: "#40B04C",
  client: "#0070B9",
  premiere: "#7661A7",
  company: "#FF1AFF",
  cancelled: "#C8102E",
};

export const RESERVATION_STATUS = {
  pending_availability: { short: "POR CONFIRMAR", label: "Disponibilidad de fecha", color: TIMELINE_COLORS.available, fill: false },
  date_requested: { short: "POR CONFIRMAR", label: "Solicitud de fecha", color: TIMELINE_COLORS.agreement, fill: false },
  date_confirmed: { short: "CONFIRMADA", label: "Confirmación de fecha", color: TIMELINE_COLORS.agreement, fill: false },
  contract_pending: { short: "CONTRATOS", label: "Firma de contratos pendiente", color: TIMELINE_COLORS.agreement, fill: true },
  deposit_pending: { short: "RESERVA 20%", label: "Pago de la reserva pendiente", color: TIMELINE_COLORS.agreement, fill: true },
  deposit_paid: { short: "FACTURA 20%", label: "Factura de la reserva (20%)", color: TIMELINE_COLORS.client, fill: false },
  balance_invoice_pending: { short: "FACTURA SALDO", label: "Factura por el saldo (80%)", color: TIMELINE_COLORS.client, fill: true },
  balance_pending: { short: "ESTRENO", label: "Estreno de función pendiente", color: TIMELINE_COLORS.premiere, fill: true },
  show_completed: { short: "SALDO PENDIENTE", label: "Pago del saldo pendiente", color: TIMELINE_COLORS.client, fill: true },
  balance_paid: { short: "SALDO PAGADO", label: "Pago del saldo confirmado", color: TIMELINE_COLORS.client, fill: true },
  // Estados legacy del tramo con la compañía (hoy ese tramo son documentos
  // en paralelo, ver `companyTrackActionFrom`).
  company_invoice_pending: { short: "FACTURA CÍA.", label: "Factura de la compañía pendiente", color: TIMELINE_COLORS.company, fill: true },
  company_payment_pending: { short: "PAGO CÍA.", label: "Pago a la compañía pendiente", color: TIMELINE_COLORS.company, fill: false },
  company_paid: { short: "CERRADA", label: "Pagos a la compañía realizados", color: TIMELINE_COLORS.company, fill: true },
  cancelled: { short: "CANCELADA", label: "Fecha cancelada", color: TIMELINE_COLORS.cancelled, fill: true },
};

// Rol que debe mover cada estado hacia el siguiente. Sale de las acciones del
// backend (`ReservationViewSet`) y coincide con el campo `role` de STEPS en
// ReservationDetail.vue:
//   manager   -> administración o compañía de teatro (`_company_or_admin_required`)
//   owner     -> administración o cliente comprador   (`_owner_required`)
//   admin     -> sólo la administración               (`_admin_required`)
//   contracts -> la administración los arma y envía; después firman el cliente
//                y la compañía, cada uno el suyo (ver `contractsActionFrom`)
//   null      -> nadie: el flujo espera o ya terminó
export const ACTION_ROLE = {
  pending_availability: "admin", // confirm_date
  date_requested: "admin", // confirm_date
  date_confirmed: null, // transitorio: salta a contract_pending
  contract_pending: "contracts", // send_contracts / accept_contract
  deposit_pending: "owner", // upload_deposit_proof
  deposit_paid: "manager", // upload_reservation_invoice
  balance_invoice_pending: "manager", // generate_balance_invoice
  balance_pending: "manager", // complete_show (sólo pasada la fecha)
  show_completed: "owner", // upload_balance_proof
  // Desde acá lo que falta es el tramo con la compañía (comprobante del 80% y
  // factura), que no es un estado: ver `companyTrackActionFrom`.
  balance_paid: null,
  company_invoice_pending: null, // legacy
  company_payment_pending: null, // legacy
  company_paid: null, // flujo cerrado
  cancelled: null,
};

// Orden del flujo principal (Reservation.FLOW_ORDER del backend). Los estados
// legacy del tramo con la compañía cuentan como "saldo pagado".
export const FLOW_ORDER = [
  "pending",
  "pending_availability",
  "date_requested",
  "date_confirmed",
  "contract_pending",
  "deposit_pending",
  "deposit_paid",
  "balance_invoice_pending",
  "balance_pending",
  "show_completed",
  "balance_paid",
  "company_invoice_pending",
  "company_payment_pending",
  "company_paid",
];

// ¿La reserva llegó (o pasó) por `target`? Una cancelada responde por el
// estado desde el que se canceló (`cancelledFromStatus`).
export function reached(reservation, target) {
  if (!reservation) return false;
  let status = String(reservation.status || "").toLowerCase();
  if (status === "cancelled") status = String(reservation.cancelledFromStatus || "").toLowerCase();
  return FLOW_ORDER.indexOf(status) >= FLOW_ORDER.indexOf(target);
}

// Tramo administración <-> compañía de teatro, en paralelo al cobro al
// cliente (que no lo ve). ¿Le toca algo a quien mira?
//   admin   -> transferir el anticipo (10%) con el 20% pagado, y el saldo
//              (80%) con el 80% pagado;
//   manager -> facturar cada transferencia una vez hecha.
export function companyTrackActionFrom(reservation, viewer = {}) {
  if (!reservation) return false;
  const status = String(reservation.status || "").toLowerCase();
  if (status === "cancelled" || status === "company_paid") return false;
  const { isAdmin = false, isManager = false } = viewer;
  if (isAdmin) {
    return (
      (reached(reservation, "deposit_paid") && !reservation.companyAdvanceProofUrl) ||
      (reached(reservation, "balance_paid") && !reservation.companyPaymentProofUrl)
    );
  }
  if (isManager) {
    return (
      (!!reservation.companyAdvanceProofUrl && !reservation.companyAdvanceInvoiceUrl) ||
      (!!reservation.companyPaymentProofUrl && !reservation.companyInvoiceUrl)
    );
  }
  return false;
}

// ¿La fecha ya pasó? Compara en texto para no arrastrar zonas horarias.
function datePassed(iso) {
  if (!iso) return false;
  const today = new Date();
  const t = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(
    today.getDate()
  ).padStart(2, "0")}`;
  return String(iso).slice(0, 10) < t;
}

// ¿Esta reserva está esperando una acción de quien mira la pantalla?
// Alimenta los contadores rojos del control de ventas (mockup "9 venta").
//
// viewer: { isAdmin, isManager, isClient } tal como los expone el store.
// functionDate: fecha de la función (ISO); sólo se usa para el estreno.
// reservation: la reserva (GraphQL, camelCase); sólo se usa para los contratos.
export function needsActionFrom(status, viewer = {}, functionDate = null, reservation = null) {
  const key = String(status || "").toLowerCase();
  if (companyTrackActionFrom(reservation, viewer)) return true;
  const role = ACTION_ROLE[key];
  if (!role) return false;
  // El estreno no pide nada hasta que la fecha quedó atrás; antes de eso el
  // hito es sólo una espera y marcarlo sería un falso positivo.
  if (key === "balance_pending" && !datePassed(functionDate)) return false;
  const { isAdmin = false, isManager = false, isClient = false } = viewer;
  if (role === "admin") return isAdmin;
  if (role === "manager") return isManager;
  if (role === "owner") return isAdmin || isClient;
  if (role === "contracts") return contractsActionFrom(reservation, viewer);
  return false;
}

// Firma de contratos: antes de enviarlos le toca a la administración; después,
// a quien todavía no firmó el suyo (cliente o compañía).
function contractsActionFrom(reservation, viewer) {
  const { isAdmin = false, isManager = false, isClient = false } = viewer;
  if (!reservation || !reservation.contractSentAt) return isAdmin;
  if (isClient) return !reservation.contractSignedAt;
  if (isManager && !isAdmin) return !reservation.companyContractSignedAt;
  return false;
}

// Estado por defecto para claves desconocidas.
const FALLBACK = { short: "RESERVA", label: "Reserva", color: "#9e9e9e", fill: true };

export function statusInfo(status) {
  return RESERVATION_STATUS[String(status || "").toLowerCase()] || FALLBACK;
}

// Entradas para la leyenda "ESTADOS DE RESERVAS PARA FUNCIONES".
// Incluye "DISPONIBLE" (sin reserva) al inicio.
export const LEGEND_ITEMS = [
  { short: "DISPONIBLE", color: "#bdbdbd", fill: false },
  ...Object.values(RESERVATION_STATUS),
];
